import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  WhatsAppEmbeddedSignupEvent, 
  FacebookAuthResponse, 
  ConnectionStatus, 
  FlowState, 
  SessionData,
  WhatsAppBusinessData 
} from '@/types/whatsapp';
import { 
  WhatsAppApiService,
  StorageService, 
  LoggerService, 
  type LogEntry 
} from '@/services';
import { WHATSAPP_CONSTANTS, FLOW_STATES, WORKFLOW_STEPS } from '@/constants';
import { CliengoQueryKey } from '@cliengo/mfe-utils/hooks';
import { getCliengoService } from '@cliengo/mfe-utils/services';
import { invalidateQuery } from '@/clients/reactQueryClient';
import { useWebsiteId } from './useWebsiteId';

export const useWhatsAppIntegration = () => {
  const websiteId = useWebsiteId();
  const [devStateOverride, setDevStateOverride] = useState<any>(null);

  // State management
  const [flowState, setFlowState] = useState<FlowState>({
    currentStep: FLOW_STATES.INITIAL,
    isLoading: false,
    error: null,
    success: false,
    cancelled: false
  });

  const [sessionData, setSessionData] = useState<SessionData>({
    sessionInfo: null,
    sdkResponse: null,
    authResponse: null,
    timestamp: new Date()
  });

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    isConnected: false
  });

  const [whatsappBusinessData, setWhatsappBusinessData] = useState<WhatsAppBusinessData>({
    workflow_step: WORKFLOW_STEPS.INITIAL,
    workflow_loading: false,
    templates: [],
    templates_loading: false,
    creating_template: false,
    message_history: [],
    template_send_responses: [],
  });

  const [localStorageData, setLocalStorageData] = useState<any>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [sessionInfoResponse, setSessionInfoResponse] = useState<string>('');
  const [sdkResponse, setSdkResponse] = useState<string>('');
  
  // Phone number in use modal state
  const [showPhoneNumberInUseModal, setShowPhoneNumberInUseModal] = useState(false);
  const [phoneNumberInUse, setPhoneNumberInUse] = useState<string | undefined>(undefined);

  // Refs
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionDataRef = useRef(sessionData);
  const connectionStatusRef = useRef(connectionStatus);
  
  // Update refs whenever state changes
  useEffect(() => {
    sessionDataRef.current = sessionData;
  }, [sessionData]);
  
  useEffect(() => {
    connectionStatusRef.current = connectionStatus;
  }, [connectionStatus]);

  // Setup logger subscription
  useEffect(() => {
    return LoggerService.subscribe((log) => {
          setLogs(prev => [...prev, log]);
        });
  }, []);

  // Load data from localStorage on initialization
  useEffect(() => {
    if (!websiteId) {
      return;
    } // Wait for websiteId to be available
    
    const savedData = StorageService.validateAndGetStorageData(websiteId);
    setLocalStorageData(savedData);
    
    if (savedData) {
      LoggerService.info('Datos validados y cargados desde localStorage', { 
        websiteId, 
        dataWebsiteId: savedData.websiteId,
        data: savedData
      });
      
      // Restore WhatsApp Business data from localStorage structure
      const whatsappData = savedData.whatsapp_business_onboarding;
      const facebookData = savedData.facebook_login_callback;
      
      if (whatsappData && facebookData) {
        setWhatsappBusinessData(prev => ({
          ...prev,
          waba_id: whatsappData.waba_id,
          business_id: whatsappData.business_id,
          code: facebookData.authResponse?.code,
          workflow_step: savedData.access_token?.token ? WORKFLOW_STEPS.SUBSCRIBE_WEBHOOKS : WORKFLOW_STEPS.INITIAL,
          workflow_loading: false
        }));
      }
      
      // Log if we already have access token
      if (savedData.access_token?.token) {
        LoggerService.info('Access token encontrado en localStorage - pasos 1 y 2 marcados como completados', {
          token: savedData.access_token.token.substring(0, 20) + '...',
          obtained_at: savedData.access_token.obtained_at
        });
      }
      
      // Restore connection status
      setConnectionStatus({
        isConnected: true,
        phoneNumberId: undefined,
        wabaId: whatsappData?.waba_id,
        connectedAt: new Date(whatsappData?.timestamp || savedData.last_updated),
        authCode: facebookData?.authResponse?.code,
        userId: facebookData?.authResponse?.userID
      });
      
      // Set flow state to show the 4-step workflow
      setFlowState({
        currentStep: FLOW_STATES.BUSINESS_ONBOARDING_COMPLETED,
        isLoading: false,
        error: null,
        success: true,
        cancelled: false
      });
    } else {
      LoggerService.info('No hay datos válidos en localStorage para este websiteId', { websiteId });
    }
  }, [websiteId]);

  // Clear timeout helper
  const clearTimeoutAndReset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // API operations using services
  const exchangeCodeForToken = async (code: string) => {
    try {
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_loading: true, 
        workflow_step: WORKFLOW_STEPS.EXCHANGE_TOKEN 
      }));
      
      LoggerService.info('Intercambiando código por token de acceso');
      
      const token = await WhatsAppApiService.exchangeCodeForToken(code);
      
      setWhatsappBusinessData(prev => ({ ...prev, access_token: token }));
      StorageService.updateAccessToken(token, websiteId);
      
      LoggerService.info('Token de acceso obtenido exitosamente', { 
        token: token.substring(0, 20) + '...' 
      });
      
      setWhatsappBusinessData(prev => ({ 
        ...prev,
        workflow_loading: false,
        workflow_step: WORKFLOW_STEPS.SUBSCRIBE_WEBHOOKS 
      }));
      
      return token;
    } catch (error) {
      LoggerService.error('Error al intercambiar código por token', error);
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_error: 'Error al obtener token de acceso' 
      }));
      throw error;
    }
  };

  const subscribeToWebhooks = async (wabaId: string, accessToken: string) => {
    try {
      LoggerService.info('Suscribiendo a webhooks de WhatsApp');
      
      const data = await WhatsAppApiService.subscribeToWebhooks(wabaId, accessToken);
      
      setWhatsappBusinessData(prev => ({ ...prev, webhook_subscription: data }));
      LoggerService.info('Suscripción a webhooks exitosa', data);
      
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_step: WORKFLOW_STEPS.GET_PHONE_NUMBERS 
      }));
      
      return data;
    } catch (error) {
      LoggerService.error('Error al suscribir a webhooks', error);
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_error: 'Error al suscribir a webhooks' 
      }));
      throw error;
    }
  };

  const getPhoneNumbers = async (wabaId: string, accessToken: string) => {
    try {
      LoggerService.info('Obteniendo números de teléfono');
      
      const phoneNumbers = await WhatsAppApiService.getPhoneNumbers(wabaId, accessToken);
      
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        phone_numbers: phoneNumbers,
        selected_phone_number: phoneNumbers[0].id
      }));
      
      LoggerService.info('Números de teléfono obtenidos', { count: phoneNumbers.length });
      
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_step: WORKFLOW_STEPS.SEND_MESSAGE 
      }));
      
      return phoneNumbers;
    } catch (error) {
      LoggerService.error('Error al obtener números de teléfono', error);
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_error: 'Error al obtener números de teléfono' 
      }));
      throw error;
    }
  };

  const sendTestMessage = async (phoneNumberId: string, accessToken: string, to: string, message: string) => {
    try {
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_step: WORKFLOW_STEPS.SEND_MESSAGE 
      }));
      
      LoggerService.info('Enviando mensaje de prueba');
      
      const data = await WhatsAppApiService.sendMessage(phoneNumberId, accessToken, to, message);
      
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        test_message_result: data,
        workflow_step: WORKFLOW_STEPS.COMPLETED,
        message_history: [
          ...(prev.message_history || []),
          {
            timestamp: new Date(),
            type: 'regular' as const,
            phone_number: to,
            message_text: message,
            response: data
          }
        ]
      }));
      
      LoggerService.info('Mensaje de prueba enviado exitosamente', data);
      return data;
    } catch (error) {
      LoggerService.error('Error al enviar mensaje de prueba', error);
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_error: 'Error al enviar mensaje de prueba' 
      }));
      throw error;
    }
  };

  // Function to check if externalId is available
  const checkExternalIdAvailability = async (externalId: string): Promise<boolean> => {
    try {
      LoggerService.info('Verificando disponibilidad del externalId', { externalId });
      
      const cliengoService = getCliengoService();
      const response = await cliengoService.http.get(`/websites/whatsappBiz?externalId=${externalId}`);
      
      LoggerService.info('Respuesta de verificación de externalId', response);
      
      return response.data.isAvailable === true;
    } catch (error) {
      LoggerService.error('Error al verificar disponibilidad del externalId', error);
      return false;
    }
  };

  // Add missing updateWebsiteConfig function
  const updateWebsiteConfig = async (websiteId: string, configData: {
    fbPermanentPageAccessToken: string;
    whatsappNumber: string;
    externalId: string;
    type: string;
    wabaId: string;
  }) => {
    try {
      LoggerService.info('Actualizando configuración del sitio web', { websiteId, configData });
      
      // Use CliengoClient updateWebsiteConfig method from mfe-utils
      const cliengoService = getCliengoService();
      const result = await cliengoService.updateWebsiteConfig(websiteId, configData);
      
      LoggerService.info('Configuración del sitio web actualizada exitosamente', result);
      
      // Invalidate website queries to refresh the data
      await invalidateQuery([CliengoQueryKey.websites]);
      await invalidateQuery([CliengoQueryKey.website, websiteId]);
      return result;
    } catch (error) {
      LoggerService.error( 'Error al actualizar la configuración del sitio web', error);
      throw error;
    }
  };

  // Complete workflow function
  const runCompleteWorkflow = async (testPhoneNumber: string, testMessage: string) => {
    try {
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        workflow_loading: true, 
        workflow_error: undefined 
      }));
      
      const existingToken = StorageService.getAccessToken();
      let accessToken = existingToken;
      
      if (!accessToken) {
        LoggerService.info('Primera vez enviando mensaje - ejecutando setup completo');
        
        const savedData = StorageService.getStorageData();
        const code = savedData?.facebook_login_callback?.authResponse?.code;
        
        if (!code) {
          throw new Error('No se encontró el código de autorización en localStorage');
        }
        
        LoggerService.info('Código obtenido del localStorage', { 
          code: code.substring(0, 20) + '...' 
        });
        
        // Step 1: Exchange code for token
        accessToken = await exchangeCodeForToken(code);
        
        // Step 2: Subscribe to webhooks
        await subscribeToWebhooks(whatsappBusinessData.waba_id!, accessToken);
      } else {
        LoggerService.info('Token existente encontrado - saltando pasos de setup', { 
          token: accessToken.substring(0, 20) + '...' 
        });
        
        setWhatsappBusinessData(prev => ({ 
          ...prev, 
          workflow_step: WORKFLOW_STEPS.GET_PHONE_NUMBERS 
        }));
      }
      
      // Step 3: Get phone numbers
      const phoneNumbers = await getPhoneNumbers(whatsappBusinessData.waba_id!, accessToken);
      
      // Step 4: Send message
      await sendTestMessage(phoneNumbers[0].id, accessToken, testPhoneNumber, testMessage);
      
      setWhatsappBusinessData(prev => ({ ...prev, workflow_loading: false }));
      LoggerService.info('Mensaje enviado exitosamente');
      
    } catch (error) {
      setWhatsappBusinessData(prev => ({ ...prev, workflow_loading: false }));
      LoggerService.error('Error al enviar mensaje', error);
    }
  };

  // Template operations
  const getMessageTemplates = async () => {
    try {
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        templates_loading: true, 
        templates_error: undefined 
      }));
      
      LoggerService.info('Obteniendo plantillas de mensajes');
      
      const accessToken = StorageService.getAccessToken();
      if (!accessToken) {
        throw new Error('No se encontró el access token');
      }
      
      const templates = await WhatsAppApiService.getMessageTemplates(
        whatsappBusinessData.waba_id!, 
        accessToken
      );
      
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        templates,
        templates_loading: false
      }));
      
      LoggerService.info(`Plantillas obtenidas: ${templates.length}`, templates);
      
      return templates;
    } catch (error) {
      LoggerService.error('Error al obtener plantillas', error);
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        templates_loading: false,
        templates_error: (error as any).message
      }));
      throw error;
    }
  };

  const createMessageTemplate = async (templateData: any) => {
    try {
      setWhatsappBusinessData(prev => ({
        ...prev,
        creating_template: true,
        create_template_error: undefined,
        create_template_success: undefined
      }));
      
      LoggerService.info('Creando plantilla de mensaje', templateData);
      
      const accessToken = StorageService.getAccessToken();
      if (!accessToken) {
        throw new Error('No se encontró el access token');
      }
      
      const data = await WhatsAppApiService.createMessageTemplate(
        whatsappBusinessData.waba_id!,
        accessToken,
        templateData
      );
      
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        creating_template: false,
        create_template_success: data
      }));
      
      LoggerService.info('Plantilla creada exitosamente', data);
      
      // Refresh templates list
      await getMessageTemplates();

      return data;
    } catch (error) {
      LoggerService.error('Error al crear plantilla', error);
      setWhatsappBusinessData(prev => ({ 
        ...prev, 
        creating_template: false,
        create_template_error: (error as any).message
      }));
      throw error;
    }
  };

  const sendTemplateMessage = async (templateName: string, languageCode: string, phoneNumber: string) => {
    try {
      LoggerService.info('Enviando plantilla de mensaje', { templateName, languageCode, phoneNumber });
      
      const accessToken = StorageService.getAccessToken();
      if (!accessToken) {
        throw new Error('No se encontró el access token');
      }
      
      // Get phone numbers to use the first available phone_number_id
      const phoneNumbers = await getPhoneNumbers(whatsappBusinessData.waba_id!, accessToken);
      const phoneNumberId = phoneNumbers[0].id;
      
      const data = await WhatsAppApiService.sendTemplateMessage(
        phoneNumberId,
        accessToken,
        phoneNumber,
        templateName,
        languageCode
      );
      
      LoggerService.info('Plantilla enviada exitosamente', data);
      
      // Add the response to template_send_responses list
      setWhatsappBusinessData(prev => ({
        ...prev,
        template_send_responses: [
          ...(prev.template_send_responses || []),
          {
            timestamp: new Date(),
            template_name: templateName,
            phone_number: phoneNumber,
            response: data
          }
        ]
      }));

      return data;
    } catch (error) {
      LoggerService.error('Error al enviar plantilla', error);
      throw error;
    }
  };

  // Handler for FINISH event - postFinishPersist 
  const postFinishPersist = useCallback(
    async (phone_number_id: string, waba_id: string) => {
      try {
        // Read data from storage instead of making API calls
        const accessToken = StorageService.getAccessToken();
        const savedData = StorageService.getStorageData();
        const whatsappData = savedData?.whatsapp_business_onboarding;
        
        if (!accessToken) {
          LoggerService.error('No access token found in storage');
          return;
        }

        if (!whatsappData?.phone_numbers) {
          LoggerService.error('No phone numbers found in storage');
          return;
        }

        const phoneNumber = whatsappData.phone_numbers.find((p: { id: string; }) => p.id === phone_number_id) || whatsappData.phone_numbers[0];
        
        if (!phoneNumber) {
          LoggerService.error('No se pudo encontrar información del número de teléfono en storage');
          return;
        }
        
        // Verify externalId availability before updating website config
        LoggerService.info('Verificando disponibilidad del externalId antes de actualizar configuración', {
          externalId: phone_number_id
        });
        
        const isAvailable = await checkExternalIdAvailability(phone_number_id);
        
        if (!isAvailable) {
          LoggerService.error('ExternalId no está disponible, no se puede actualizar la configuración del sitio web', {
            externalId: phone_number_id,
            websiteId,
            waba_id
          });
          throw new Error(`El externalId ${phone_number_id} no está disponible para usar en este sitio web`);
        }
        
        LoggerService.info('ExternalId disponible, procediendo con la actualización de configuración', {
          externalId: phone_number_id
        });
        
        const cleanPhoneNumber = phoneNumber.display_phone_number.replace(/[+\s-]/g, '');

        const payload = {
          fbPermanentPageAccessToken: accessToken,
          whatsappNumber: cleanPhoneNumber,
          externalId: phone_number_id,
          type: 'WHATSAPP_BIZ',
          wabaId: waba_id
        };

        await updateWebsiteConfig(websiteId, payload);
        LoggerService.info('Website config updated successfully (post-FINISH)', {
          websiteId,
          waba_id,
          phone_number_id,
          phoneNumber: phoneNumber.display_phone_number,
        });
      } catch (error) {
        LoggerService.error('Failed to update website config (post-FINISH)', error);
        throw error;
      }
    },
    [websiteId, updateWebsiteConfig, checkExternalIdAvailability]
  );

  // Event handlers (keeping the same logic but using services)
  const handleFinishEvent = useCallback((data: any) => {
    LoggerService.info('WhatsApp signup completed successfully', data);
    clearTimeoutAndReset();

    try {
      const { phone_number_id, waba_id } = data;
      
      if (!phone_number_id || !waba_id) {
        throw new Error('Missing required data: phone_number_id or waba_id');
      }

      setConnectionStatus({
        isConnected: true,
        phoneNumberId: phone_number_id,
        wabaId: waba_id,
        connectedAt: new Date(),
        authCode: sessionData.authResponse?.authResponse?.code,
        userId: sessionData.authResponse?.authResponse?.userID
      });

      setFlowState({
        currentStep: FLOW_STATES.COMPLETED,
        isLoading: false,
        error: null,
        success: true,
        cancelled: false
      });

      // Reset workflow loading state
      setWhatsappBusinessData(prev => ({
        ...prev,
        workflow_loading: false,
        workflow_step: WORKFLOW_STEPS.COMPLETED
      }));

      // Call postFinishPersist to update website configuration
      postFinishPersist(phone_number_id, waba_id).catch((e) => {
        LoggerService.error('Failed to update website config (post-FINISH)', { message: e?.message || String(e) });
        
        // Show modal if externalId is not available
        if (e?.message && e.message.includes('no está disponible')) {
          // Get phone number from storage to show in the modal
          const savedData = StorageService.getStorageData();
          const whatsappData = savedData?.whatsapp_business_onboarding;
          const phoneNumber = whatsappData?.phone_numbers?.find((p: { id: string; }) => p.id === phone_number_id);
          
          setPhoneNumberInUse(phoneNumber?.display_phone_number);
          setShowPhoneNumberInUseModal(true);
          
          LoggerService.info('Showing phone number in use modal', { 
            phoneNumberId: phone_number_id,
            displayPhoneNumber: phoneNumber?.display_phone_number 
          });
        }
      });

      // Persist final connection state to localStorage
      try {
        const savedData = StorageService.getStorageData();
        const updated = {
          ...savedData,
          websiteId: websiteId,
          completed: true,
          completedAt: new Date().toISOString(),
          whatsapp_business_onboarding: {
            ...(savedData?.whatsapp_business_onboarding || {}),
            waba_id,
            phoneNumberId: phone_number_id,
            timestamp: new Date().toISOString()
          },
          last_updated: new Date().toISOString()
        };
        StorageService.setStorageData(updated);
        LoggerService.info('Persisted final connection state (completed=true)', {
          waba_id, phone_number_id
        });
      } catch (error) {
        LoggerService.error('Failed to persist connection state', error);
      }

      LoggerService.info('Connection established successfully', {
        phoneNumberId: phone_number_id,
        wabaId: waba_id
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      LoggerService.error('Error processing finish event', error);
      setFlowState(prev => ({
        ...prev,
        isLoading: false,
        error: `Error al completar la conexión: ${errorMessage}`
      }));
    }
  }, [sessionData.authResponse, clearTimeoutAndReset, postFinishPersist]);

  // Handler for FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING event
  const handleFinishWhatsAppBusinessOnboarding = useCallback(async (data: any) => {
    LoggerService.info('WhatsApp Business onboarding completed', data);
    
    const { waba_id, business_id } = data;
    
    // Get code from current session or stored auth response
    const code = sessionDataRef.current.authResponse?.authResponse?.code || 
                connectionStatusRef.current.authCode;
    const userId = sessionDataRef.current.authResponse?.authResponse?.userID || 
                  connectionStatusRef.current.userId;
    
    // Log what we have available
    LoggerService.info('Available data for storage', {
      waba_id,
      business_id,
      code: code ? code.substring(0, 20) + '...' : 'Not available',
      userId: userId || 'Not available'
    });
    
    // Update WhatsApp Business data
    setWhatsappBusinessData(prev => ({
      ...prev,
      waba_id,
      business_id,
      code,
      workflow_step: WORKFLOW_STEPS.INITIAL
    }));
    
    // Update connection status
    setConnectionStatus({
      isConnected: true,
      phoneNumberId: undefined, // Will be obtained later
      wabaId: waba_id,
      connectedAt: new Date(),
      authCode: code,
      userId: userId
    });
    
    // Update flow state
    setFlowState({
      currentStep: FLOW_STATES.BUSINESS_ONBOARDING_COMPLETED,
      isLoading: false,
      error: null,
      success: true,
      cancelled: false
    });
    
    // Save to localStorage
    StorageService.updateWhatsAppBusinessData({
      waba_id,
      business_id,
      code,
      phone_number_id: undefined,
      user_id: userId
    }, websiteId);
    
    LoggerService.info('WhatsApp Business onboarding data stored successfully', {
      waba_id,
      business_id,
      code: code ? 'Available' : 'Missing - will need to re-authenticate',
      userId: userId || 'Not available'
    });
  }, [websiteId, exchangeCodeForToken, getPhoneNumbers, updateWebsiteConfig]);

  // Handler for CANCEL event
  const handleCancelEvent = useCallback((data: any) => {
    const { current_step } = data;
    LoggerService.warn(`User cancelled at step: ${current_step}`, data);
    
    clearTimeoutAndReset();

    setFlowState({
      currentStep: current_step || FLOW_STATES.CANCELLED_BY_USER,
      isLoading: false,
      error: null,
      success: false,
      cancelled: true
    });

    setConnectionStatus({ isConnected: false });
    StorageService.clearStorageData();
  }, [clearTimeoutAndReset]);

  // Handler for ERROR event
  const handleErrorEvent = useCallback((data: any) => {
    const { error_message } = data;
    LoggerService.error(`WhatsApp signup error: ${error_message}`, data);
    
    clearTimeoutAndReset();

    setFlowState({
      currentStep: FLOW_STATES.ERROR,
      isLoading: false,
      error: error_message || 'Error desconocido durante el proceso',
      success: false,
      cancelled: false
    });

    setConnectionStatus({ isConnected: false });
    StorageService.clearStorageData();
  }, [clearTimeoutAndReset]);
  
  // Automatic token exchange and website config update after login
  const handlePostLoginSetup = useCallback(async (code: string) => {
    try {
      LoggerService.info('Starting automatic token exchange and setup');
      
      // Get business ID from storage (if available from previous onboarding)
      const savedData = StorageService.getStorageData();
      const businessId = savedData?.whatsapp_business_onboarding?.business_id;
      const wabaId = savedData?.whatsapp_business_onboarding?.waba_id;
      
      if (!wabaId) {
        LoggerService.info('WABA ID not found, waiting for business onboarding completion');
        return;
      }
      
      // Exchange code for token
      const accessToken = await exchangeCodeForToken(code);
      
      // Subscribe to webhooks
      await subscribeToWebhooks(wabaId, accessToken);
      
      // Get phone numbers
      const phoneNumbers = await getPhoneNumbers(wabaId, accessToken);
      
      if (phoneNumbers && phoneNumbers.length > 0) {
        const phoneNumber = phoneNumbers[0];
        
        // Store phone numbers data in localStorage for display
        StorageService.updateWhatsAppBusinessData({
          phone_numbers: phoneNumbers,
          selected_phone_number: phoneNumber.id
        }, websiteId);
        
        LoggerService.info('Token exchange and setup completed successfully', {
          phoneNumber: phoneNumber.display_phone_number,
          phoneNumberId: phoneNumber.id,
          wabaId,
          businessId
        });
        
        // Call handleFinishEvent to trigger the normal completion flow
        handleFinishEvent({ waba_id: wabaId, phone_number_id: phoneNumber.id });
      }
    } catch (error) {
      LoggerService.error('Error during automatic post-login setup', error);
    }
  }, [exchangeCodeForToken, subscribeToWebhooks, getPhoneNumbers, handleFinishEvent]);

  // Facebook login callback handler
  const fbLoginCallback = useCallback((response: FacebookAuthResponse) => {
    LoggerService.info('Facebook login callback received', response);
    
    // Save Facebook login callback data to localStorage
    StorageService.updateFacebookLoginData(response, websiteId);
    
    // Store raw SDK response
    setSdkResponse(JSON.stringify(response, null, 2));
    
    // Clear timeout since we got a response
    clearTimeoutAndReset();
    
    setSessionData(prev => ({
      ...prev,
      sdkResponse: response,
      authResponse: response,
      timestamp: new Date()
    }));
    
    // Always store the auth code and userId if available, regardless of status
    if (response.authResponse) {
      const {code} = response.authResponse;
      const userId = response.authResponse.userID;
      LoggerService.info(`Authorization code received: ${code?.substring(0, 20)}...`);
      
      // Store code and userId in connection status immediately
      setConnectionStatus(prev => ({
        ...prev,
        authCode: code,
        userId: userId
      }));
    }
    
    // Check the response status
    if (response.status === 'connected' && response.authResponse) {
      const { code, userID } = response.authResponse;
      LoggerService.info('Authorization code received', { ...response });
      
      setFlowState(prev => ({
        ...prev,
        isLoading: false,
        currentStep: FLOW_STATES.AUTHENTICATION_COMPLETE,
        cancelled: false
      }));
      
      // Update flow state to indicate we're waiting for WhatsApp setup
      setTimeout(() => {
        setFlowState(prev => ({
          ...prev,
          currentStep: FLOW_STATES.WAITING_FOR_WHATSAPP_SETUP
        }));
      }, 1000);
      
      // Trigger automatic setup if we already have business data
      if (code) {
        handlePostLoginSetup(code).catch(error => {
          LoggerService.error('Automatic setup failed after login', error);
        });
      }
      
    } else if (response.status === 'not_authorized' || response.status === 'unknown') {
      // For unknown status, if we have authResponse, continue the flow
      if (response.authResponse && response.authResponse.code) {
        const { code, userID } = response.authResponse;
        LoggerService.info('Status unknown but auth code received, continuing flow');
        setFlowState(prev => ({
          ...prev,
          isLoading: false,
          currentStep: FLOW_STATES.AUTHENTICATION_COMPLETE,
          cancelled: false
        }));
        
        // Update flow state to indicate we're waiting for WhatsApp setup
        setTimeout(() => {
          setFlowState(prev => ({
            ...prev,
            currentStep: FLOW_STATES.WAITING_FOR_WHATSAPP_SETUP
          }));
        }, 1000);
        
        // Trigger automatic setup
        if (code) {
          handlePostLoginSetup(code).catch(error => {
            LoggerService.error('Automatic setup failed after login (unknown status)', error);
          });
        }
      } else {
        // Check if user is already connected before showing popup_closed
        const savedData = StorageService.getStorageData();
        const hasCompleted = !!savedData?.completed;
        const hasPhoneId = !!savedData?.whatsapp_business_onboarding?.phoneNumberId;
        const isAlreadyConnected = hasCompleted && hasPhoneId;
        
        if (isAlreadyConnected) {
          LoggerService.info('User closed popup but connection already exists, restoring connection state');
          // Restore the existing connection instead of showing error
          setFlowState({
            currentStep: FLOW_STATES.COMPLETED,
            isLoading: false,
            error: null,
            success: true,
            cancelled: false
          });
          setConnectionStatus(prev => ({
            ...prev,
            isConnected: true,
            phoneNumberId: savedData.whatsapp_business_onboarding.phoneNumberId,
            wabaId: savedData.whatsapp_business_onboarding.waba_id || prev.wabaId
          }));
        } else {
          LoggerService.warn('User denied permission or closed popup - clearing partial state');
          setFlowState({
            currentStep: FLOW_STATES.POPUP_CLOSED,
            isLoading: false,
            error: null,
            success: false,
            cancelled: true
          });
          StorageService.clearStorageData();
          setConnectionStatus({ isConnected: false });
        }
      }
      
    } else {
      const errorMessage = 'No se pudo completar la autenticación con Facebook';
      LoggerService.error(errorMessage, response);
      setFlowState(prev => ({
        ...prev,
        error: errorMessage,
        currentStep: FLOW_STATES.AUTHENTICATION_FAILED
      }));
    }
  }, [clearTimeoutAndReset, handlePostLoginSetup, websiteId]);
  
  const launchWhatsAppSignup = useCallback(() => {
    LoggerService.info('Launching WhatsApp signup flow');
    
    setFlowState({
      currentStep: FLOW_STATES.INITIALIZING,
      isLoading: true,
      error: null,
      success: false,
      cancelled: false
    });
    
    setWhatsappBusinessData({
      workflow_step: WORKFLOW_STEPS.INITIAL,
      workflow_loading: false
    });

    if (typeof window.FB === 'undefined') {
      const errorMessage = 'Facebook SDK no está disponible. Por favor, recarga la página.';
      LoggerService.error(errorMessage);
      setFlowState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        currentStep: FLOW_STATES.SDK_UNAVAILABLE
      }));
      return;
    }

    try {
      setFlowState(prev => ({
        ...prev,
        currentStep: FLOW_STATES.LAUNCHING_FACEBOOK_LOGIN
      }));

      timeoutRef.current = setTimeout(() => {
        LoggerService.warn('Timeout reached - popup may have been closed');
        setFlowState({
          currentStep: FLOW_STATES.POPUP_CLOSED,
          isLoading: false,
          error: null,
          success: false,
          cancelled: true
        });
        StorageService.clearStorageData();
        setConnectionStatus({ isConnected: false });
      }, WHATSAPP_CONSTANTS.TIMEOUT_DURATION);
      
      window.FB.login(fbLoginCallback, {
        config_id: WHATSAPP_CONSTANTS.CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        scope: 'whatsapp_business_management,whatsapp_business_messaging,ads_read',
        extras: {
          setup: {},
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
        }
      });

      setFlowState(prev => ({
        ...prev,
        currentStep: FLOW_STATES.FACEBOOK_LOGIN_LAUNCHED
      }));
    } catch (error) {
      const errorMessage = 'Error al lanzar el proceso de autenticación';
      LoggerService.error(errorMessage, error);
      setFlowState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        currentStep: FLOW_STATES.LAUNCH_FAILED
      }));
    }
  }, [clearTimeoutAndReset, fbLoginCallback]);

  const disconnect = useCallback(async () => {
    LoggerService.info('Disconnecting WhatsApp integration');
    clearTimeoutAndReset();
    StorageService.clearStorageData();
    
    // Update website config to clear WhatsApp integration
    try {
      const payload = {
        whatsappNumber: '',
        type: 'EXTERNAL'
      };
      await updateWebsiteConfig(websiteId, payload);
      LoggerService.info('Website config cleared during disconnect', { websiteId });
    } catch (error) {
      LoggerService.error('Failed to clear website config during disconnect', error);
    }
    
    setConnectionStatus({ isConnected: false });
    setSessionData({
      sessionInfo: null,
      sdkResponse: null,
      authResponse: null,
      timestamp: new Date()
    });
    setFlowState({
      currentStep: FLOW_STATES.INITIAL,
      isLoading: false,
      error: null,
      success: false,
      cancelled: false
    });
    setWhatsappBusinessData({
      workflow_step: WORKFLOW_STEPS.INITIAL,
      workflow_loading: false
    });
    setSessionInfoResponse('');
    setSdkResponse('');
  }, [clearTimeoutAndReset, updateWebsiteConfig, websiteId]);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  // Handle phone number in use modal dismiss
  const handlePhoneNumberInUseModalDismiss = useCallback(() => {
    LoggerService.info('Phone number in use modal dismissed, resetting connection status');
    
    // Hide the modal
    setShowPhoneNumberInUseModal(false);
    setPhoneNumberInUse(undefined);
    
    // Reset connection status and flow state
    setConnectionStatus({ isConnected: false });
    setFlowState({
      currentStep: FLOW_STATES.INITIAL,
      isLoading: false,
      error: null,
      success: false,
      cancelled: false
    });
    
    // Clear any WhatsApp data
    setWhatsappBusinessData({
      workflow_step: WORKFLOW_STEPS.INITIAL,
      workflow_loading: false,
      templates: [],
      templates_loading: false,
      creating_template: false,
      message_history: [],
      template_send_responses: [],
    });
    
    // Clear localStorage data
    StorageService.clearStorageData();
    setLocalStorageData(null);
    
    // Clear session data
    setSessionData({
      sessionInfo: null,
      sdkResponse: null,
      authResponse: null,
      timestamp: new Date()
    });
    setSessionInfoResponse('');
    setSdkResponse('');
    
    LoggerService.info('Connection reset completed after phone number in use error');
  }, []);

  const retryConnection = useCallback(() => {
    LoggerService.info('Retrying WhatsApp connection');
    disconnect();
    setTimeout(() => {
      launchWhatsAppSignup();
    }, 1000);
  }, [disconnect, launchWhatsAppSignup]);

  // Development function to override states
  const setDevelopmentState = useCallback((stateOverride: any) => {
    if (stateOverride.clearLocalStorage) {
      StorageService.clearStorageData();
      setLocalStorageData(null);
    }

    if (stateOverride.mockLocalStorage) {
      const mockDataWithWebsiteId = {
        ...stateOverride.mockLocalStorage,
        websiteId: websiteId
      };
      StorageService.setStorageData(mockDataWithWebsiteId);
      setLocalStorageData(mockDataWithWebsiteId);
    }

    if (stateOverride.flowState) {
      setFlowState(stateOverride.flowState);
    }

    if (stateOverride.whatsappBusinessData) {
      setWhatsappBusinessData(prev => ({ ...prev, ...stateOverride.whatsappBusinessData }));
    }

    if (stateOverride.connectionStatus) {
      setConnectionStatus(stateOverride.connectionStatus);
    }

    setDevStateOverride(stateOverride);
  }, [websiteId]);

  // Main message handler for WhatsApp events
  const handleWhatsAppMessage = useCallback((event: MessageEvent) => {
    // Security check - only accept messages from Facebook domains
    if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
      return;
    }

    try {
      const data: WhatsAppEmbeddedSignupEvent = JSON.parse(event.data);
      
      // Store raw session info response
      setSessionInfoResponse(JSON.stringify(data, null, 2));
      
      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        // Update session info
        setSessionData(prev => ({
          ...prev,
          sessionInfo: data,
          timestamp: new Date()
        }));

        // Handle different events with enhanced logging
        if (data.event === 'FINISH') {
          const { phone_number_id, waba_id } = data.data;
          LoggerService.info(`Phone number ID: ${phone_number_id}, WhatsApp business account ID: ${waba_id}`);
          handleFinishEvent(data.data);
        } else if (data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
          const { waba_id, business_id } = data.data;
          LoggerService.info(`WhatsApp Business onboarding completed - WABA ID: ${waba_id}, Business ID: ${business_id}`);
          handleFinishWhatsAppBusinessOnboarding(data.data);
        } else if (data.event === 'CANCEL') {
          const { current_step } = data.data;
          LoggerService.warn(`Cancel at ${current_step}`);
          handleCancelEvent(data.data);
        } else if (data.event === 'ERROR') {
          const { error_message } = data.data;
          LoggerService.error(`Error: ${error_message}`);
          handleErrorEvent(data.data);
        } else {
          LoggerService.warn('Unknown event type received', data);
        }
      } else {
        LoggerService.warn('Received non-WhatsApp message', data);
      }
    } catch (err) {
      // Handle non-JSON responses
      LoggerService.info('Received non-JSON response', { data: event.data });
      setSessionInfoResponse(event.data);
      
      // Some responses might be simple strings indicating steps
      if (typeof event.data === 'string') {
        setFlowState(prev => ({
          ...prev,
          currentStep: event.data
        }));
      }
    }
  }, [handleFinishEvent, handleFinishWhatsAppBusinessOnboarding, handleCancelEvent, handleErrorEvent]);
  

  // Setup event listener for messages
  useEffect(() => {
    window.addEventListener('message', handleWhatsAppMessage);
    
    return () => {
      window.removeEventListener('message', handleWhatsAppMessage);
    };
  }, [handleWhatsAppMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeoutAndReset();
    };
  }, [clearTimeoutAndReset]);

  return {
    flowState,
    sessionData,
    connectionStatus,
    whatsappBusinessData,
    localStorageData,
    logs,
    sessionInfoResponse,
    sdkResponse,
    launchWhatsAppSignup,
    disconnect,
    retryConnection,
    clearLogs,
    cancelProcess: disconnect,
    runCompleteWorkflow,
    getMessageTemplates,
    createMessageTemplate,
    sendTemplateMessage,
    clearMessageHistory: () => setWhatsappBusinessData(prev => ({ ...prev, message_history: [] })),
    clearTemplateSendResponses: () => setWhatsappBusinessData(prev => ({ ...prev, template_send_responses: [] })),
    setDevelopmentState,
    showPhoneNumberInUseModal,
    phoneNumberInUse,
    handlePhoneNumberInUseModalDismiss
  };
};
